import {
  CheckOperator,
  MemoryDTO,
  StepCheckOperatorType,
} from '../../../Book/MemoryBank/memoryBankTypes';

const MAX_WORDS = 5;
const MAX_LENGTH = 48;
const HUB_REPLAY_WORDS = 3;

/** Naming labels — names rendered on the canvas, intentionally short. */
const LABEL = {
  yes: 'Yes',
  no: 'No',
  else: 'Else',
  has: 'Has',
  replay: 'Replay',
} as const;

const BOOL_VALUE = {
  true: 'true',
  false: 'false',
} as const;

const SEPARATOR = '_';

/**
 * Maps a string-enum VALUE back to its PascalCase KEY name.
 * E.g. StepCheckOperatorType.AtLeast === 'atLeast', and OPERATOR_PASCAL_LABEL['atLeast'] === 'AtLeast'.
 * Built from the enum itself so we never duplicate the operator list.
 */
const OPERATOR_PASCAL_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(StepCheckOperatorType).map(([key, value]) => [value, key]),
);

type CheckShape = {
  operator: string,
  variableId?: number | string,
  value?: { type?: string, variableId?: Array<string | number> } | unknown,
};

/**
 * Mirrors StepCheckSwitch. Two RHS formats coexist because UI emits
 * intValue/varValue while the DB stores number/variable (see ChoiceMemory
 * which normalizes both); we accept either.
 */
type OutcomeShape = {
  value?: boolean | string | number,
  operator?: string,
  intValue?: string | number,
  varValue?: string | number,
  number?: string | number,
  variable?: string | number,
};

export type BuildBranchTitleContext =
  | { kind: 'answer', text: string }
  | { kind: 'hubOption', title: string }
  | { kind: 'hubReplay', optionTitle: string }
  | { kind: 'hubOverride', optionTitle: string }
  | { kind: 'check', check: CheckShape, outcome: OutcomeShape };

function pascalCase(text: string, maxWords = MAX_WORDS, maxLength = MAX_LENGTH): string {
  if (!text) return '';
  const words = String(text)
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, ''))
    .filter(Boolean)
    .slice(0, maxWords)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  let name = words.join('');
  if (name.length > maxLength) {
    let truncated = '';
    for (const w of words) {
      if (truncated.length + w.length > maxLength) break;
      truncated += w;
    }
    name = truncated || name.slice(0, maxLength);
  }
  return name;
}

function findMemoryName(memories: MemoryDTO[], id: number | string | undefined): string | null {
  if (id === undefined || id === null || id === '') return null;
  const numeric = Number(id);
  if (Number.isNaN(numeric)) return null;
  const found = memories.find((m) => Number(m.id) === numeric);
  return found?.name ?? null;
}

function join(...parts: string[]): string {
  return parts.filter(Boolean).join(SEPARATOR);
}

function booleanLabel(value: boolean | string | number | undefined): string {
  if (value === BOOL_VALUE.true) return LABEL.yes;
  if (value === BOOL_VALUE.false) return LABEL.no;
  return '';
}

function buildPlayerHas(outcome: OutcomeShape): string {
  const label = booleanLabel(outcome.value);
  return label ? join(LABEL.has, label) : '';
}

function buildMinMax(operator: string, outcome: OutcomeShape, memories: MemoryDTO[]): string {
  const rawId = outcome.value;
  if (typeof rawId === 'boolean') return '';
  const winnerName = findMemoryName(memories, rawId);
  if (!winnerName) return '';
  // Reuse CheckOperator[op].title — same 'Highest'/'Lowest' string the UI shows.
  const prefix = CheckOperator[operator as StepCheckOperatorType]?.title ?? '';
  return prefix ? join(prefix, winnerName) : '';
}

function isFilledLiteral(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

// `varValue === 0` is the "no variable selected" sentinel in the UI
// (see ConditionListForm.tsx validation: `!varValue || varValue === 0`).
function isFilledVariableRef(value: unknown): boolean {
  return isFilledLiteral(value) && value !== 0 && value !== '0';
}

function buildConditionListOutcome(
  leftVarName: string,
  outcome: OutcomeShape,
  memories: MemoryDTO[],
): string {
  // Otherwise row has no operator.
  if (!outcome.operator) {
    return join(leftVarName, LABEL.else);
  }
  const opLabel = OPERATOR_PASCAL_LABEL[outcome.operator] ?? pascalCase(outcome.operator);

  const literalRhs = isFilledLiteral(outcome.intValue) ? outcome.intValue : outcome.number;
  if (isFilledLiteral(literalRhs)) {
    const numClean = String(literalRhs).replace(/[^A-Za-z0-9]/g, '');
    return numClean ? join(leftVarName, `${opLabel}${numClean}`) : '';
  }

  const variableRhs = isFilledVariableRef(outcome.varValue) ? outcome.varValue : outcome.variable;
  if (isFilledVariableRef(variableRhs)) {
    const rhsVarName = findMemoryName(memories, variableRhs as string | number);
    return rhsVarName ? join(leftVarName, `${opLabel}${rhsVarName}`) : '';
  }

  return '';
}

function buildCheckTitle(check: CheckShape, outcome: OutcomeShape, memories: MemoryDTO[]): string {
  const op = check.operator;

  // `hasCompleted` belongs to AchievementCheckOperatorType, not step checks —
  // intentionally not handled here.
  if (op === StepCheckOperatorType.PlayerHas) {
    return buildPlayerHas(outcome);
  }

  if (op === StepCheckOperatorType.Min || op === StepCheckOperatorType.Max) {
    return buildMinMax(op, outcome, memories);
  }

  if (op === StepCheckOperatorType.ConditionList) {
    const leftVarName = findMemoryName(memories, check.variableId);
    return leftVarName ? buildConditionListOutcome(leftVarName, outcome, memories) : '';
  }

  // Single comparison (equal/notEqual/greater/atLeast/less/atMost) — boolean outcome.
  const leftVarName = findMemoryName(memories, check.variableId);
  if (!leftVarName) return '';
  const label = booleanLabel(outcome.value);
  return label ? join(leftVarName, label) : '';
}

// Case-insensitive match — mirrors MySQL default collation on Branch.title uniq.
export function pickNextFreeTitle(root: string, taken: Set<string>): string {
  const lowered = new Set<string>();
  taken.forEach((t) => lowered.add(String(t).toLowerCase()));

  if (!root) {
    let max = 0;
    let seenAny = false;
    taken.forEach((t) => {
      const s = String(t);
      if (/^\d+$/.test(s)) {
        const n = Number(s);
        if (!Number.isNaN(n)) {
          seenAny = true;
          if (n > max) max = n;
        }
      }
    });
    return seenAny ? String(max + 1) : '';
  }

  if (!lowered.has(root.toLowerCase())) return root;

  const rootLower = root.toLowerCase();
  let max = 1;
  taken.forEach((t) => {
    const s = String(t).toLowerCase();
    if (!s.startsWith(rootLower)) return;
    const tail = s.slice(rootLower.length);
    if (!/^\d+$/.test(tail)) return;
    const n = Number(tail);
    if (!Number.isNaN(n) && n > max) max = n;
  });
  return `${root}${max + 1}`;
}

// All-digit input returns '' so the caller treats the whole title as a counter.
export function rootOfBranchTitle(branchTitle: string | null | undefined): string {
  if (!branchTitle) return '';
  return String(branchTitle).replace(/\d+$/, '');
}

export function buildBranchTitle(
  context: BuildBranchTitleContext,
  memories: MemoryDTO[],
): string {
  switch (context.kind) {
    case 'answer':
      return pascalCase(context.text);
    case 'hubOption':
      return pascalCase(context.title);
    case 'hubReplay': {
      const tail = pascalCase(context.optionTitle, HUB_REPLAY_WORDS);
      if (!tail) return LABEL.replay;
      // Guard: avoid Replay_Replay when the source already starts with "Replay"
      // (e.g. author named the option "Replay" or override text begins with it).
      if (tail.toLowerCase().startsWith(LABEL.replay.toLowerCase())) return tail;
      return join(LABEL.replay, tail);
    }
    case 'hubOverride':
      return pascalCase(context.optionTitle);
    case 'check':
      return buildCheckTitle(context.check, context.outcome, memories);
    default:
      return '';
  }
}
